import React, { useState, useEffect } from 'react';
import {
    Drawer, Box, Typography, Button, TextField,
    FormGroup, FormControlLabel, Checkbox, IconButton, useTheme,
    Alert, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import employee_service from '../services/employee.service';
import { usePreferences } from '../contexts/PreferencesContext';

const AddEmployeeDrawer = ({ open, onClose, workplaces, onEmployeeCreated }) => {
    const theme = useTheme();
    const { t } = usePreferences();
    const [formData, setFormData] = useState({ name: '', position: '', email: '', phone_number: '' });
    const [assignedWorkplaces, setAssignedWorkplaces] = useState(new Set());
    const [sendInvite, setSendInvite] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setFormData({ name: '', position: '', email: '', phone_number: '' });
            setAssignedWorkplaces(new Set());
            setSendInvite(true);
            setError(null);
            setSubmitting(false);
        }
    }, [open]);

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleWorkplaceToggle = (workplaceId) => {
        const newAssigned = new Set(assignedWorkplaces);
        if (newAssigned.has(workplaceId)) {
            newAssigned.delete(workplaceId);
        } else {
            newAssigned.add(workplaceId);
        }
        setAssignedWorkplaces(newAssigned);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!formData.name.trim() || !formData.phone_number.trim()) {
            setError(t('addEmployeeDrawer.errorRequired'));
            return;
        }

        setSubmitting(true);
        try {
            const employeeResponse = await employee_service.createEmployee({
                ...formData,
                send_invite: sendInvite,
            });
            const employeeId = employeeResponse.data.id;
            if (assignedWorkplaces.size > 0) {
                await employee_service.assignWorkplaces(employeeId, Array.from(assignedWorkplaces));
            }
            if (employeeResponse.data.invite_sms === 'failed') {
                // Zamestnanec je vytvorený, len SMS zlyhala — informuj a zavri
                alert(t('addEmployeeDrawer.inviteSmsFailed'));
            }
            onEmployeeCreated(); // This will trigger fetching data and closing the drawer
        } catch (err) {
            console.error("Error creating employee or assignments", err);
            const serverMessage = err?.response?.data?.message;
            setError(serverMessage || t('addEmployeeDrawer.errorGeneric'));
        } finally {
            setSubmitting(false);
        }
    };

    const modalTextFieldStyle = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
            },
        },
        '& label.Mui-focused': {
            color: theme.palette.primary.main,
        },
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            keepMounted={false}
            disableRestoreFocus={true}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 400, md: 500 },
                    backgroundColor: theme.palette.background.default,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                    transition: 'background-color 0.3s ease, border-color 0.3s ease',
                }
            }}
        >
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{t('addEmployeeDrawer.title')}</Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 2 }}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}
                        <TextField name="name" label={t('addEmployeeDrawer.name')} value={formData.name} onChange={handleFormChange} fullWidth required margin="dense" variant="outlined" sx={modalTextFieldStyle} />
                        <TextField name="position" label={t('addEmployeeDrawer.position')} value={formData.position} onChange={handleFormChange} fullWidth margin="dense" variant="outlined" sx={modalTextFieldStyle} />
                        <TextField name="email" label={t('addEmployeeDrawer.email')} value={formData.email} onChange={handleFormChange} fullWidth margin="dense" variant="outlined" sx={modalTextFieldStyle} />
                        <TextField
                            name="phone_number"
                            label={t('addEmployeeDrawer.phone')}
                            value={formData.phone_number}
                            onChange={handleFormChange}
                            fullWidth
                            required
                            margin="dense"
                            variant="outlined"
                            helperText={t('addEmployeeDrawer.phoneHelper')}
                            sx={modalTextFieldStyle}
                        />

                        <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>{t('addEmployeeDrawer.assignWorkplaces')}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {t('addEmployeeDrawer.workplaceRequiredHint')}
                        </Typography>
                        <FormGroup>
                            {workplaces.map(wp => (
                                <FormControlLabel
                                    key={wp.id}
                                    control={
                                        <Checkbox
                                            checked={assignedWorkplaces.has(wp.id)}
                                            onChange={() => handleWorkplaceToggle(wp.id)}
                                            sx={{ '&.Mui-checked': { color: theme.palette.primary.main } }}
                                        />
                                    }
                                    label={wp.name}
                                />
                            ))}
                        </FormGroup>
                        {workplaces.length > 0 && assignedWorkplaces.size === 0 && (
                            <Alert severity="warning" sx={{ mt: 1, borderRadius: '12px' }}>
                                {t('addEmployeeDrawer.noWorkplaceWarning')}
                            </Alert>
                        )}

                        <Box sx={{ mt: 2, p: 1.5, borderRadius: '12px', bgcolor: theme.palette.action.hover }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={sendInvite}
                                        onChange={(e) => setSendInvite(e.target.checked)}
                                        sx={{ '&.Mui-checked': { color: theme.palette.primary.main } }}
                                    />
                                }
                                label={t('addEmployeeDrawer.sendInvite')}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4 }}>
                                {t('addEmployeeDrawer.sendInviteHint')}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}>
                        <Button onClick={onClose} sx={{ color: 'grey.700', borderRadius: '12px' }}>{t('common.cancel')}</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={{
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                borderRadius: '12px',
                                boxShadow: 'none',
                                '&:hover': {
                                    bgcolor: 'primary.dark',
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            {t('addEmployeeDrawer.create')}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
};

export default AddEmployeeDrawer;
